import React from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import DnsIcon from "@mui/icons-material/Dns";
import SecurityIcon from "@mui/icons-material/Security";
import RouterIcon from "@mui/icons-material/Router";
import QueueIcon from "@mui/icons-material/Queue";
import InfoIcon from "@mui/icons-material/Info";

const RecordDisplay = ({ data }) => {
  const StatusChip = ({ active }) => (
    <Chip
      icon={active ? <CheckCircleIcon /> : <CancelIcon />}
      label={active ? "Active" : "Inactive"}
      color={active ? "success" : "error"}
      size="small"
      sx={{ fontWeight: "bold" }}
    />
  );

  const InfoCard = ({ title, icon, children, status = null }) => (
    <Paper
      elevation={0}
      variant="outlined"
      sx={{
        p: 3,
        borderRadius: 3,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {icon}
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1rem" }}>
            {title}
          </Typography>
        </Box>
        {status}
      </Box>
      <Divider sx={{ mb: 2 }} />
      <Box>{children}</Box>
    </Paper>
  );

  const getStatusColor = (status) => {
    if (!status) return "text.primary";
    const s = status.toUpperCase();
    switch (s) {
      case "NORMAL":
        return "success.main";
      case "HIGH":
      case "EXTREME_HIGH":
        return "error.main";
      default:
        return "text.primary";
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Grid
        container
        spacing={3}
        sx={{
          alignItems: "flex-start",
        }}
      >
        {/* Domain Status Card */}
        <Grid item xs={12} md={4}>
          <InfoCard
            title="Domain Status"
            icon={<DnsIcon color="primary" />}
            status={<StatusChip active={data.active} />}
          >
            <Typography variant="body2" color="text.secondary">
              The domain is currently {data.active ? "fully functional" : "inactive"} in the monitor.
            </Typography>
          </InfoCard>
        </Grid>

        {/* MX Records Card */}
        <Grid item xs={12} md={8}>
          <InfoCard title="MX Records" icon={<RouterIcon color="primary" />}>
            {data.mx && data.mx.length > 0 ? (
              <Grid container spacing={1}>
                {data.mx.map((mx, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <CheckCircleIcon fontSize="small" color="success" />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{mx.host}</Typography>
                      <Typography variant="caption" color="text.secondary">({mx.priority})</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="error">No MX records found.</Typography>
            )}
          </InfoCard>
        </Grid>

        {/* SPF Policy Card */}
        <Grid item xs={12} md={6}>
          <InfoCard title="SPF Policy" icon={<SecurityIcon color="primary" />}>
            <Typography
              variant="body2"
              sx={{
                p: 1.5,
                bgcolor: "grey.50",
                borderRadius: 1,
                fontFamily: "monospace",
                wordBreak: "break-all",
                border: "1px solid",
                borderColor: "grey.200",
              }}
            >
              {data.spf || "No SPF record present"}
            </Typography>
          </InfoCard>
        </Grid>

        {/* Mail Queue Card */}
        <Grid item xs={12} md={6}>
          <InfoCard title="Mail Queue Status" icon={<QueueIcon color="primary" />}>
            <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, mb: 1 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: getStatusColor(data.mailq_status),
                }}
              >
                {data.mailq_status || "Unknown"}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                overall status
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Aggregated health check across incoming, outgoing, and mailbox server queues.
            </Typography>
          </InfoCard>
        </Grid>

        {/* Ping/Reachability Card */}
        <Grid item xs={12}>
          <InfoCard title="Server Reachability (Ping)" icon={<InfoIcon color="primary" />}>
            {data.ping_response && data.ping_response.length > 0 ? (
              <Grid container spacing={2}>
                {data.ping_response.map((ping, index) => (
                  <Grid item xs={12} sm={6} md={3} key={index}>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: "grey.50", borderStyle: "dashed" }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        {ping.server_name}
                      </Typography>
                      <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">Loss</Typography>
                          <Typography variant="body2" color={ping.pocket_loss > 0 ? "error" : "success"} fontWeight={700}>
                            {ping.pocket_loss}%
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: "right" }}>
                          <Typography variant="caption" color="text.secondary" display="block">Pockets</Typography>
                          <Typography variant="body2">{ping.pocket_sent}/{ping.pocket_received}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary">No reachability data available.</Typography>
            )}
          </InfoCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default RecordDisplay;
